import { useEffect, useState, useMemo } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, Navigation, ArrowRight, Trophy, Dumbbell, PartyPopper, Images, LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PhotoSlider from "@/components/PhotoSlider";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import api, { money } from "@/lib/api";
import { HERO_IMG, RELEASE, ARTICLES, LOGO } from "@/lib/brand";

const CATS = [
  { key: "all", label: "All", icon: LayoutGrid },
  { key: "training", label: "Training", icon: Dumbbell },
  { key: "tournament", label: "Tournament / Blitz", icon: Trophy },
  { key: "special", label: "Special Events", icon: PartyPopper },
];

function fmtDate(iso) {
  if (!iso) return "TBD";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function Home() {
  usePageTitle("Lansdowne Youth Rugby - Youth Rugby Club in New York", "The fastest growing, affordable youth rugby program in Yonkers / Woodlawn, New York for ages 3-17. Register for Fall Rugby, training, tournaments and special events.");
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [album, setAlbum] = useState(null);
  const [albumView, setAlbumView] = useState("grid");
  const [slide, setSlide] = useState(0);
  const openAlbum = (a) => { setAlbum(a); setSlide(0); };
  const photoCount = album?.photos.length || 0;
  const goPrev = () => setSlide((s) => (s - 1 + photoCount) % photoCount);
  const goNext = () => setSlide((s) => (s + 1) % photoCount);
  const [article, setArticle] = useState(null);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const now = new Date();

  const albums = useMemo(() => {
    const map = new Map();
    gallery.forEach((g) => {
      const key = g.event_id || "club";
      if (!map.has(key)) {
        const ev = events.find((e) => e.id === g.event_id);
        map.set(key, {
          key,
          title: g.event_title || (g.event_id ? "Event Photos" : "Club Photos"),
          cover: ev?.thumbnail || g.image_url,
          photos: [],
        });
      }
      map.get(key).photos.push(g);
    });
    return Array.from(map.values());
  }, [gallery, events]);

  return (
    <div className="bg-brand-black min-h-screen text-white">
      <Navbar />

      {/* HERO - split layout mirroring the original site */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 lg:pt-8 pb-12 lg:pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 animate-fade-up">
            <div className="inline-flex flex-col items-center mb-6">
              <img src={LOGO} alt="Lansdowne RFC New York Youth Rugby Club" className="h-56 w-auto object-contain mb-4" data-testid="hero-logo" />
              <div className="bg-brand-gold text-brand-black font-extrabold text-xs tracking-[0.16em] uppercase px-4 py-2">
                Lansdowne Rugby Football Club New York
              </div>
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.88] text-white mb-6">
              YOUTH RUGBY<br /><span className="text-brand-red">FOR AGES 3–17</span>
            </h1>
            <p className="text-lg text-white/75 max-w-xl">
              We're the fastest growing, affordable youth rugby program in Yonkers / Woodlawn. We hold a 10-week program every Fall and Spring, with experienced coaches — all sessions at Tibbetts Brook Park. All experience levels welcome.
            </p>
          </div>
          <div className="order-2 relative animate-fade-up w-full">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full bg-brand-red/90 -z-10 hidden lg:block" />
              <div className="absolute -bottom-5 -left-5 w-24 h-24 border-4 border-brand-gold -z-10 hidden lg:block" />
              <img src={HERO_IMG} alt="Lansdowne Youth Rugby players" className="relative w-full h-[340px] lg:h-[460px] object-cover border border-white/10" />
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              <Button className="bg-brand-red hover:bg-brand-red/90 text-white rounded-none h-14 px-8 text-base font-bold uppercase tracking-wider" onClick={() => navigate("/register")} data-testid="hero-register-btn">
                Register Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" className="rounded-none h-14 px-8 text-base font-bold uppercase tracking-wider border-white/30 bg-transparent text-white hover:bg-white/10" onClick={() => document.getElementById("programs")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-programs-btn">
                View Programs
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <SectionHeading kicker="Join the Club" title="OUR PROGRAMS" />
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {programs.map((p, i) => (
            <div key={p.id} className="group border border-white/10 bg-white/[0.03] hover:border-brand-red transition-colors" data-testid={`program-card-${i}`}>
              <div className="h-52 overflow-hidden">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-3xl tracking-wide">{p.name}</h3>
                  <span className="font-display text-3xl text-brand-gold">{money(p.price_cents)}</span>
                </div>
                <p className="text-sm text-white/70 mb-6 min-h-[60px]">{p.description}</p>
                {p.is_full ? (
                  <Button disabled className="w-full bg-white/10 border border-white/20 text-white/50 rounded-none font-bold uppercase tracking-wider cursor-not-allowed" data-testid={`program-full-${i}`}>
                    Full
                  </Button>
                ) : (
                  <Button className="w-full bg-transparent border border-brand-red text-white hover:bg-brand-red rounded-none font-bold uppercase tracking-wider" onClick={() => navigate("/register")} data-testid={`program-signup-${i}`}>
                    Sign Up{typeof p.spots_left === "number" && p.spots_left <= 5 ? ` · ${p.spots_left} left` : ""}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <PhotoSlider />

      {/* EVENTS */}
      <section id="events" className="bg-white/[0.02] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <SectionHeading kicker="What's On" title="EVENTS & SESSIONS" />
          <Tabs defaultValue="all" className="mt-12">
            <TabsList className="bg-transparent gap-2 flex-wrap h-auto justify-start p-0">
              {CATS.map((c) => (
                <TabsTrigger key={c.key} value={c.key} className="rounded-none border border-white/15 data-[state=active]:bg-brand-red data-[state=active]:text-white data-[state=active]:border-brand-red text-white/70 px-5 py-2.5 font-bold uppercase tracking-wider text-xs" data-testid={`events-tab-${c.key}`}>
                  <c.icon className="h-4 w-4 mr-2" /> {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {CATS.map((c) => {
              const list = c.key === "all" ? events : events.filter((e) => e.category === c.key);
              return (
                <TabsContent key={c.key} value={c.key} className="mt-10">
                  {list.length === 0 ? (
                    <p className="text-white/50 py-10">No {c.key === "all" ? "events" : c.label.toLowerCase()} scheduled yet. Check back soon.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {list.map((e, i) => {
                        const past = e.start_time && new Date(e.start_time) < now;
                        return (
                          <div key={e.id} className="border border-white/10 bg-brand-black overflow-hidden group" data-testid={`event-card-${c.key}-${i}`}>
                            <div className="h-44 overflow-hidden relative">
                              <img src={e.thumbnail} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                              <Badge className={`absolute top-3 left-3 rounded-none uppercase text-[10px] tracking-wider ${past ? "bg-white/20" : "bg-brand-gold text-brand-black"}`}>
                                {past ? "Past" : "Upcoming"}
                              </Badge>
                            </div>
                            <div className="p-6">
                              <h3 className="font-display text-2xl tracking-wide mb-3">{e.title}</h3>
                              <p className="text-sm text-white/60 mb-4 line-clamp-2">{e.description}</p>
                              <div className="space-y-2 text-sm text-white/70">
                                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-red" /> {fmtDate(e.start_time)}</div>
                                {e.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-red" /> {e.location.name}</div>}
                              </div>
                              {e.location?.maps_url && (
                                <a href={e.location.maps_url} target="_blank" rel="noreferrer" data-testid={`event-directions-${c.key}-${i}`}>
                                  <Button className="w-full mt-5 bg-transparent border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black rounded-none font-bold uppercase tracking-wider text-xs">
                                    <Navigation className="h-4 w-4 mr-2" /> Get Directions
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* GALLERY / ALBUMS */}
      <section id="gallery" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <SectionHeading kicker="On The Pitch" title="PHOTO ALBUMS" />
        {albums.length === 0 ? (
          <p className="text-white/50 mt-8">Photos coming soon.</p>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((a, i) => (
              <button key={a.key} onClick={() => openAlbum(a)} className="group text-left border border-white/10 overflow-hidden hover:border-brand-red transition-colors" data-testid={`album-card-${i}`}>
                <div className="h-60 overflow-hidden relative">
                  <img src={a.cover} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="font-display text-3xl tracking-wide text-white leading-none mb-1">{a.title}</div>
                    <div className="text-brand-gold text-sm flex items-center gap-1.5"><Images className="h-4 w-4" /> {a.photos.length} photo{a.photos.length !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ALBUM LIGHTBOX */}
      <Dialog open={!!album} onOpenChange={(o) => !o && setAlbum(null)}>
        <DialogContent className="bg-brand-black border-white/15 text-white rounded-none max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="album-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4 pr-8">
              <DialogTitle className="font-display text-4xl tracking-wide">{album?.title}</DialogTitle>
              <div className="flex items-center border border-white/15">
                <button onClick={() => setAlbumView("grid")} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${albumView === "grid" ? "bg-brand-red text-white" : "text-white/60 hover:text-white"}`} data-testid="album-view-grid">
                  <LayoutGrid className="h-4 w-4" /> Grid
                </button>
                <button onClick={() => setAlbumView("slider")} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${albumView === "slider" ? "bg-brand-red text-white" : "text-white/60 hover:text-white"}`} data-testid="album-view-slider">
                  <Images className="h-4 w-4" /> Slider
                </button>
              </div>
            </div>
          </DialogHeader>

          {albumView === "grid" ? (
            <div className="columns-2 md:columns-3 gap-4 space-y-4 mt-2">
              {album?.photos.map((g, i) => (
                <div key={g.id} className="break-inside-avoid overflow-hidden border border-white/10" data-testid={`album-photo-${i}`}>
                  <img src={g.image_url} alt={g.caption || "photo"} className="w-full object-cover" loading="lazy" />
                  {g.caption && <div className="p-2 text-xs text-white/60">{g.caption}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-2 w-full min-w-0 overflow-hidden" data-testid="album-slider">
              <div className="relative bg-black/40 border border-white/10 h-[60vh] w-full overflow-hidden flex items-center justify-center">
                {album?.photos[slide] && (
                  <img src={album.photos[slide].image_url} alt={album.photos[slide].caption || "photo"} className="max-h-full max-w-full w-auto h-auto object-contain" data-testid="album-slide-image" />
                )}
                <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-brand-black/80 hover:bg-brand-red border border-white/20 p-2" data-testid="album-slide-prev" aria-label="Previous">
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-brand-black/80 hover:bg-brand-red border border-white/20 p-2" data-testid="album-slide-next" aria-label="Next">
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-3 right-3 bg-brand-black/80 border border-white/15 px-3 py-1 text-xs font-bold tracking-wider" data-testid="album-slide-counter">
                  {slide + 1} / {photoCount}
                </div>
              </div>
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {album?.photos.map((g, i) => (
                  <button key={g.id} onClick={() => setSlide(i)} className={`h-16 w-24 shrink-0 overflow-hidden border ${i === slide ? "border-brand-red" : "border-white/10 opacity-60 hover:opacity-100"}`} data-testid={`album-thumb-${i}`}>
                    <img src={g.image_url} alt="thumb" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEWS */}
      <section id="news" className="bg-white/[0.02] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <SectionHeading kicker="Lansdowne RFC NY" title="LATEST NEWS" />
          <div className="grid lg:grid-cols-2 gap-12 mt-12 items-start">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-brand-red/90 -z-10 hidden lg:block" />
              <img src={RELEASE.image} alt="Paul O'Connell" className="relative w-full object-cover border border-white/10" data-testid="news-image" />
              <div className="mt-6 border border-white/10 p-6 bg-brand-black">
                <div className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-4">Event Details</div>
                <ul className="space-y-2 text-sm text-white/85">
                  <li><span className="text-white/50 uppercase text-xs tracking-wider mr-2">Date</span> {RELEASE.details.Date}</li>
                  <li><span className="text-white/50 uppercase text-xs tracking-wider mr-2">Time</span> {RELEASE.details.Time}</li>
                  <li><span className="text-white/50 uppercase text-xs tracking-wider mr-2">Where</span> {RELEASE.details.Location}</li>
                </ul>
                <Button className="mt-5 bg-brand-red hover:bg-brand-red/90 text-white rounded-none font-bold uppercase tracking-wider" onClick={() => navigate("/register")} data-testid="news-register-btn">
                  Register to Attend <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
            <div data-testid="news-release">
              <div className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-4">{RELEASE.date}</div>
              <h3 className="font-display text-4xl sm:text-5xl leading-[1.05] mb-6">{RELEASE.headline}</h3>
              <div className="space-y-4 text-white/75 text-base leading-relaxed">
                {RELEASE.body.map((p) => (<p key={p.slice(0, 32)}>{p}</p>))}
                <p className="text-white font-semibold border-l-2 border-brand-gold pl-4">{RELEASE.closing}</p>
              </div>
            </div>
          </div>

          {ARTICLES.length > 0 && (
            <div className="mt-16">
              <div className="text-white/50 text-xs font-bold tracking-[0.2em] uppercase mb-6">Club Archive</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {ARTICLES.map((a) => (
                  <button key={a.id} onClick={() => setArticle(a)} className="group text-left border border-white/10 bg-brand-black overflow-hidden hover:border-brand-red transition-colors" data-testid={`article-card-${a.id}`}>
                    <div className="h-52 overflow-hidden">
                      <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <div className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-2">{a.date}</div>
                      <h4 className="font-display text-2xl tracking-wide mb-3 group-hover:text-brand-red transition-colors">{a.title}</h4>
                      <span className="text-sm text-white/60 inline-flex items-center gap-1">Read More <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ARTICLE DIALOG */}
      <Dialog open={!!article} onOpenChange={(o) => !o && setArticle(null)}>
        <DialogContent className="bg-brand-black border-white/15 text-white rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl sm:text-4xl tracking-wide leading-tight">{article?.title}</DialogTitle>
          </DialogHeader>
          {article && (
            <div className="mt-2">
              <img src={article.image} alt={article.title} className="w-full h-64 object-cover border border-white/10 mb-6" />
              <div className="space-y-4 text-white/75 leading-relaxed">
                {article.blocks.map((b) => (b.h ? <h4 key={b.h.slice(0, 32)} className="font-display text-2xl text-brand-gold tracking-wide pt-2">{b.h}</h4> : <p key={b.p.slice(0, 32)}>{b.p}</p>))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ABOUT */}
      <section id="about" className="bg-brand-red">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h2 className="font-display text-5xl sm:text-6xl text-white mb-6">BUILT ON RUGBY TRADITION</h2>
          <p className="text-white/90 text-lg leading-relaxed">
            Lansdowne Youth Rugby was founded in 2022 by the Old Boys of Lansdowne RFC New York, with a simple but powerful goal:
            to teach the next generation the game of rugby and the values that come with it. Led entirely by volunteer coaches,
            we develop rugby skills alongside respect, sportsmanship and camaraderie — keeping tuition affordable so opportunity stays open to everyone.
          </p>
          <Button className="mt-10 bg-brand-black hover:bg-black text-white rounded-none h-14 px-10 text-base font-bold uppercase tracking-wider" onClick={() => navigate("/register")} data-testid="about-register-btn">
            Join The Family
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SectionHeading({ kicker, title }) {
  return (
    <div>
      <div className="text-brand-gold text-xs font-bold tracking-[0.3em] uppercase mb-3">{kicker}</div>
      <h2 className="font-display text-5xl sm:text-6xl text-white flex items-center gap-4">
        <span className="w-12 h-1 bg-brand-red inline-block" />{title}
      </h2>
    </div>
  );
}
